import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, iconSetQuartzLight, themeQuartz } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { CommentsService } from '../../service/comments.service';
import { ExcelImportService, SchemaNode, SpreadsheetRows } from '../../service/excel-import.service';
import { SettingsService } from '../../service/settings.service';
import { ImportedTridyUpdate, TridysService } from '../../service/tridys.service';
import { localizeTridyImportError } from '../tridy-import-translation';

interface UpdateRow {
  rowNumber: number;
  identifier?: unknown;
  targetId?: string;
  payload: Record<string, unknown>;
  errors: string[];
}

@Component({
  selector: 'app-update-tridys',
  standalone: false,
  templateUrl: './update-tridys.component.html',
  styleUrls: ['./update-tridys.component.css']
})
export class UpdateTridysComponent {
  readonly tridyIdMapping = '__tridy_id__';
  contentTypes: any[] = [];
  contentTypeId = '';
  schemaKeys: string[] = [];
  activeMappingHeader: string | null = null;
  showFullFieldPaths = false;
  contentTypeSearchTerm = '';
  spreadsheet?: SpreadsheetRows;
  columnMappings: Record<string, string> = {};
  rows: UpdateRow[] = [];
  headerErrors: string[] = [];
  currentStep: 1 | 2 | 3 = 1;
  preparing = false;
  updating = false;
  updateProgress = { completed: 0, total: 0 };
  updatedCount = 0;
  validationGridRows: any[] = [];
  validationColumnDefs: ColDef[] = [];
  agGridLocaleText = AG_GRID_LOCALE_EN;
  gridLanguage = 'en';
  readonly validationPageSizeSelector = [25, 50, 100];
  readonly gridTheme = themeQuartz.withPart(iconSetQuartzLight).withParams({
    backgroundColor: '#ffffff', headerBackgroundColor: '#f8fafc', foregroundColor: 'rgb(46, 55, 66)',
    borderColor: '#d9e1ea', oddRowBackgroundColor: '#fbfcfe', headerFontSize: 12, headerFontWeight: 650,
    spacing: 6, rowBorder: true, wrapperBorder: false, wrapperBorderRadius: 0, fontFamily: 'Roboto, sans-serif'
  });
  readonly validationDefaultColDef: ColDef = {
    sortable: true, resizable: true, filter: true, minWidth: 150,
    valueFormatter: params => this.formatValue(params.value),
    tooltipValueGetter: params => this.formatValue(params.value)
  };

  constructor(
    private router: Router,
    private settingsService: SettingsService,
    private excelImportService: ExcelImportService,
    private tridysService: TridysService,
    private commentsService: CommentsService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.updateLanguage(this.translate.currentLang() ?? 'en');
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(change => this.updateLanguage(change.lang));
    this.contentTypes = this.settingsService.settingsContentTypesWithId || [];
    this.updateSchemaKeys();
    if (!this.contentTypes.length) {
      this.settingsService.observeContextsContentTypesFirestoreWithId(this.settingsService.contextId$)
        .subscribe(contentTypes => {
          this.contentTypes = contentTypes || [];
          this.updateSchemaKeys();
        });
    }
  }

  private updateLanguage(language: string): void {
    this.gridLanguage = language.startsWith('de') ? 'de' : language.startsWith('es') ? 'es' : 'en';
    this.agGridLocaleText = language.startsWith('de') ? AG_GRID_LOCALE_DE
      : language.startsWith('es') ? AG_GRID_LOCALE_ES : AG_GRID_LOCALE_EN;
    this.refreshValidationGrid();
  }

  private get dateLocale(): string {
    const language = this.translate.currentLang() ?? 'en';
    return language.startsWith('de') ? 'de-DE' : language.startsWith('es') ? 'es-ES' : 'en-GB';
  }

  localizeValidationError(error: string): string {
    const exact: Record<string, string> = {
      'Ordne eine Spalte als DPP ID zu.': 'mapIdColumn',
      'Nur eine Spalte darf als DPP ID zugeordnet werden.': 'onlyOneIdColumn',
      'Ordne mindestens ein Datenfeld zu, das aktualisiert werden soll.': 'mapPayloadField',
      'Keine bestehende DPP ID im aktuellen Context und in dieser Vorlage gefunden.': 'idNotFound',
      'Der Identifikator ist nicht eindeutig und kann nicht sicher aktualisiert werden.': 'ambiguousId',
      'Der Identifikator ist leer oder ungültig.': 'invalidId',
      'Keine Datenfelder für die Aktualisierung zugeordnet.': 'noPayloadFields',
      'Der Identifikator kommt in der Importdatei mehrfach vor.': 'duplicateId'
    };
    if (exact[error]) return this.translate.instant(`tridys.updateWizard.errors.${exact[error]}`);
    const differentTemplate = error.match(/^Die DPP gehört zur Vorlage "(.*)"\. Wähle diese in Schritt 1\.$/);
    if (differentTemplate) return this.translate.instant('tridys.updateWizard.errors.differentTemplate', { template: differentTemplate[1] });
    if (error === 'Bestehende DPPs konnten nicht geladen werden. Prüfe die Firestore-Indexe für diese Abfrage.') {
      return this.translate.instant('tridys.updateWizard.loadExistingFailed');
    }
    const duplicateField = error.match(/^Das Zielfeld "(.*)" wurde mehrfach zugeordnet\.$/);
    if (duplicateField) return this.translate.instant('tridys.updateWizard.errors.duplicateTarget', { field: duplicateField[1] });
    return localizeTridyImportError(error, this.translate);
  }

  get selectedContentType(): any { return this.contentTypes.find(contentType => contentType.id === this.contentTypeId); }
  get filteredContentTypes(): any[] {
    const term = this.contentTypeSearchTerm.trim().toLowerCase();
    return !term ? this.contentTypes : this.contentTypes.filter(contentType =>
      String(contentType?.displayName || '').toLowerCase().includes(term)
      || String(contentType?.id || '').toLowerCase().includes(term));
  }
  private updateSchemaKeys(): void {
    const schema = this.selectedContentType?.['schema']?.['properties'] as SchemaNode[] | undefined;
    this.schemaKeys = schema?.length ? this.excelImportService.getImportableSchemaKeys(schema) : [];
  }
  get validCount(): number { return this.rows.filter(row => !row.errors.length && row.targetId).length; }
  get invalidCount(): number { return this.rows.length - this.validCount; }
  get invalidRows(): UpdateRow[] { return this.rows.filter(row => row.errors.length); }
  get identifierSource(): string | undefined {
    return (this.spreadsheet?.headers || []).find(header => this.columnMappings[header] === this.tridyIdMapping);
  }
  get hasMappedPayloadFields(): boolean { return Object.values(this.columnMappings).some(value => !!value && value !== this.tridyIdMapping); }
  get updatePercent(): number { return this.updateProgress.total ? Math.round(this.updateProgress.completed / this.updateProgress.total * 100) : 0; }
  get updateDisabledReason(): string | undefined {
    if (this.updating) return this.translate.instant('tridys.updateWizard.updatingNow');
    if (!this.rows.length) return this.translate.instant('tridys.updateWizard.checkFileAndMappingFirst');
    if (this.invalidCount) return this.translate.instant('tridys.updateWizard.fixMarkedRows');
    if (!this.validCount) return this.translate.instant('tridys.updateWizard.noMatchingDpps');
    return undefined;
  }

  onContentTypeChange(): void {
    this.updateSchemaKeys();
    if (this.spreadsheet) this.initialiseMapping();
    this.rows = [];
    this.refreshValidationGrid();
  }

  async readFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.spreadsheet = await this.excelImportService.readSpreadsheet(file);
      this.initialiseMapping();
      this.rows = [];
      this.refreshValidationGrid();
      this.currentStep = 2;
    } catch (error: any) {
      this.spreadsheet = undefined;
      this.commentsService.addSnackBar.emit(error?.message
        ? localizeTridyImportError(error.message, this.translate)
        : this.translate.instant('tridys.importWizard.fileReadFailed'));
    } finally {
      input.value = '';
    }
  }

  async prepareUpdates(): Promise<void> {
    if (!this.spreadsheet || !this.contentTypeId || !this.identifierSource || this.preparing) return;
    const schema = this.selectedContentType?.['schema']?.['properties'] as SchemaNode[] | undefined;
    if (!schema?.length) {
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.importWizard.noSchema'));
      return;
    }
    this.preparing = true;
    this.headerErrors = this.mappingErrors();
    this.rows = this.spreadsheet.rows.map(row => this.createUpdateRow(row, schema));
    if (!this.headerErrors.length) {
      this.markDuplicateIdentifiers();
      try {
        const identifiers = this.rows.filter(row => !row.errors.length).map(row => row.identifier);
        const matches = await this.tridysService.findImportedTridys(this.contentTypeId, 'id', identifiers);
        this.rows.forEach(row => {
          if (row.errors.length || row.identifier === undefined) return;
          const found = matches.get(this.identifierKey(row.identifier)) || [];
          if (!found.length) row.errors.push('Keine bestehende DPP ID im aktuellen Context und in dieser Vorlage gefunden.');
          else if (found.length > 1) row.errors.push('Der Identifikator ist nicht eindeutig und kann nicht sicher aktualisiert werden.');
          else if (found[0].contentType !== this.contentTypeId) {
            const contentType = this.contentTypes.find(item => item.id === found[0].contentType);
            const name = contentType?.displayName || contentType?.name || found[0].contentType || '—';
            row.errors.push(`Die DPP gehört zur Vorlage "${name}". Wähle diese in Schritt 1.`);
          }
          else row.targetId = found[0].id;
        });
      } catch (error: any) {
        this.headerErrors.push(error?.message || 'Bestehende DPPs konnten nicht geladen werden. Prüfe die Firestore-Indexe für diese Abfrage.');
      }
    }
    this.refreshValidationGrid();
    this.currentStep = 3;
    this.preparing = false;
  }

  async updateValidRows(): Promise<void> {
    if (this.updateDisabledReason || !this.spreadsheet) return;
    const updates: ImportedTridyUpdate[] = this.rows
      .filter(row => !row.errors.length && row.targetId)
      .map(row => ({ id: row.targetId!, payload: row.payload }));
    this.updating = true;
    this.updateProgress = { completed: 0, total: updates.length };
    try {
      this.commentsService.progressSpin.emit(true);
      await this.tridysService.mergeImportedTridys(updates, this.spreadsheet.fileName,
        (completed, total) => this.updateProgress = { completed, total });
      this.updatedCount = updates.length;
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.updateWizard.updatedCount', { count: updates.length }));
    } catch (error: any) {
      this.commentsService.addSnackBar.emit(error?.message || this.translate.instant('tridys.updateWizard.updateFailed'));
    } finally {
      this.commentsService.progressSpin.emit(false);
      this.updating = false;
    }
  }

  startNewUpdate(): void {
    if (this.updating) return;
    this.spreadsheet = undefined;
    this.columnMappings = {};
    this.activeMappingHeader = null;
    this.rows = [];
    this.headerErrors = [];
    this.updatedCount = 0;
    this.updateProgress = { completed: 0, total: 0 };
    this.refreshValidationGrid();
    this.currentStep = 1;
  }

  openTridysList(): void { this.router.navigate(['home', this.settingsService.contextId$, 'tridys']); }

  private initialiseMapping(): void {
    const headers = this.spreadsheet?.headers || [];
    const schemaKeys = new Set(this.schemaKeys);
    this.activeMappingHeader = null;
    this.columnMappings = headers.reduce((mapping, header) => {
      mapping[header] = schemaKeys.has(header) ? header : '';
      return mapping;
    }, {} as Record<string, string>);
    const idHeader = headers.find(header => header.toLowerCase() === 'tridy id')
      || headers.find(header => header.toLowerCase() === 'tridyid')
      || headers.find(header => header.toLowerCase() === 'id');
    if (idHeader) this.columnMappings[idHeader] = this.tridyIdMapping;
  }

  private createUpdateRow(row: { rowNumber: number; values: Record<string, unknown> }, schema: SchemaNode[]): UpdateRow {
    const values = Object.entries(row.values).reduce((result, [header, value]) => {
      const target = this.columnMappings[header];
      if (target && target !== this.tridyIdMapping) result[target] = value;
      return result;
    }, {} as Record<string, unknown>);
    const result = this.excelImportService.validatePartialPayload(values, schema);
    const identifier = this.identifierSource ? row.values[this.identifierSource] : undefined;
    const errors = [...result.errors];
    if (identifier === undefined || identifier === null || String(identifier).trim() === '') {
      errors.push('Der Identifikator ist leer oder ungültig.');
    }
    if (!Object.keys(result.payload).length) errors.push('Keine Datenfelder für die Aktualisierung zugeordnet.');
    return { rowNumber: row.rowNumber, identifier, payload: result.payload, errors };
  }

  private mappingErrors(): string[] {
    const errors: string[] = [];
    const tridyIdMappings = Object.values(this.columnMappings).filter(value => value === this.tridyIdMapping).length;
    if (!tridyIdMappings) errors.push('Ordne eine Spalte als DPP ID zu.');
    if (tridyIdMappings > 1) errors.push('Nur eine Spalte darf als DPP ID zugeordnet werden.');
    if (!this.hasMappedPayloadFields) errors.push('Ordne mindestens ein Datenfeld zu, das aktualisiert werden soll.');
    const mapped = Object.values(this.columnMappings).filter(value => !!value && value !== this.tridyIdMapping);
    const duplicate = mapped.find((field, index) => mapped.indexOf(field) !== index);
    if (duplicate) errors.push(`Das Zielfeld "${duplicate}" wurde mehrfach zugeordnet.`);
    return errors;
  }

  private markDuplicateIdentifiers(): void {
    const identifiers = new Map<string, UpdateRow[]>();
    this.rows.forEach(row => {
      if (row.errors.length || row.identifier === undefined) return;
      const key = this.identifierKey(row.identifier);
      identifiers.set(key, [...(identifiers.get(key) || []), row]);
    });
    identifiers.forEach(rows => {
      if (rows.length < 2) return;
      rows.forEach(row => row.errors.push('Der Identifikator kommt in der Importdatei mehrfach vor.'));
    });
  }

  private refreshValidationGrid(): void {
    const headers = this.spreadsheet?.headers || [];
    this.validationGridRows = this.rows.map((row, index) => ({
      __errors: row.errors,
      __rowNumber: row.rowNumber,
      ...(this.spreadsheet?.rows[index]?.values || {})
    }));
    this.validationColumnDefs = headers.map(header => ({
      field: header,
      valueGetter: params => params.data?.[header],
      headerName: this.mappingLabel(header),
      headerTooltip: this.fullMappingLabel(header),
      width: 190,
      minWidth: 150,
      cellClassRules: { 'grid-cell-invalid': params => this.cellErrors(params.data, header).length > 0 },
      tooltipValueGetter: params => this.cellErrors(params.data, header).map(error => this.localizeValidationError(error)).join(' ') || this.formatValue(params.value)
    } as ColDef));
  }

  private mappingLabel(header: string): string {
    const target = this.columnMappings[header];
    const source = this.shortFieldPath(header);
    if (target === this.tridyIdMapping) return `${source} -> ${this.translate.instant('tridys.updateWizard.dppId')}`;
    return target ? `${source} -> ${this.shortFieldPath(target)}` : `${source} (${this.translate.instant('tridys.updateWizard.ignore')})`;
  }

  private fullMappingLabel(header: string): string {
    const target = this.columnMappings[header];
    if (target === this.tridyIdMapping) return `${header} -> ${this.translate.instant('tridys.updateWizard.dppId')}`;
    return target ? `${header} -> ${target}` : `${header} (${this.translate.instant('tridys.updateWizard.ignore')})`;
  }

  shortFieldPath(path: string): string {
    const parts = path.split('.');
    return parts.length > 1 ? `… ${parts[parts.length - 1]}` : path;
  }

  private cellErrors(row: any, header: string): string[] {
    const errors = Array.isArray(row?.__errors) ? row.__errors as string[] : [];
    const target = this.columnMappings[header];
    return errors.filter(error => {
      if (header === this.identifierSource) {
        return error.includes('Identifikator')
          || error.includes('bestehendes DPP')
          || error.includes('bestehende DPP ID')
          || error.includes('gehört zur Vorlage')
          || error.includes('nicht eindeutig')
          || error.includes('mehrfach');
      }
      return !!target && target !== this.tridyIdMapping && error.includes(target);
    });
  }

  private identifierKey(value: unknown): string { return value instanceof Date ? value.toISOString() : String(value).trim(); }
  private formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return '';
    if (value instanceof Date) return value.toLocaleDateString(this.dateLocale);
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}
